import React, { createContext, useState } from "react";
import { PowerBiReport } from "powerbi-reports/models";
import powerBiReportsService from "powerbi-reports/service";
import microsoftTeamsAccessTokenProvider from "powerbi-reports/auth";
import { Page } from "powerbi-client";
import { trackEvent, trackException } from "appilication-insights/ApplicationInsightsUtils";
import { GotPowerBiTokenEvent } from "appilication-insights/events/OpenPowerBiReport/GotPowerBiTokenEvent";
import { GotPowerBiReportsEvent } from "appilication-insights/events/OpenPowerBiReport/GotPowerBiReportsEvent";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export interface PowerBiReportsContextProps {
    powerBiAccessToken: string;
    reports: PowerBiReport[];
    selectedReport: PowerBiReport | undefined;
    setSelectedReport: (report: PowerBiReport | undefined) => void;
    initialized: boolean;
    initialize: () => Promise<void>;
    selectedReportPages: Page[] | undefined;
    selectedReportPage: Page | undefined;
    setSelectedReportPages: (pages: Page[] | undefined) => void;
    setSelectedReportPage: (page: Page | undefined) => void;
}

export const defaultPowerBiReportsDefaultProps: PowerBiReportsContextProps = {
    powerBiAccessToken: "",
    reports: [],
    selectedReport: undefined,
    setSelectedReport: () => null,
    initialized: false,
    initialize: () => new Promise<void>((_) => null),
    selectedReportPages: [],
    selectedReportPage: undefined,
    setSelectedReportPages: () => null,
    setSelectedReportPage: () => null,
};

export const PowerBiReportsContext = createContext(defaultPowerBiReportsDefaultProps);

const PowerBiReportsStore: React.FC = (props) => {
    const [initialized, setInitialized] = useState<boolean>(false);
    const [powerBiAccessToken, setPowerBiAccessToken] = useState<string>("");
    const [reports, setReports] = useState<PowerBiReport[]>([]);
    const [selectedReport, setSelectedReport] = useState<PowerBiReport>();
    const [selectedReportPages, setSelectedReportPages] = useState<Page[]>();
    const [selectedReportPage, setSelectedReportPage] = useState<Page>();

    const appInsights = useAppInsightsContext();

    const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();

    const createAccessTokenInterval = (secondsLeft: number) => {
        const intervalId = setInterval(async () => {
            const microsoftTeamsAccessToken = await microsoftTeamsAccessTokenProvider.getAccessToken();
            const tokenInfo = await powerBiReportsService.getPowerBiAccessToken(microsoftTeamsAccessToken);

            const gotTokenEvent = new GotPowerBiTokenEvent(tokenInfo.expiresIn, true);
            trackEvent(appInsights, gotTokenEvent, gotTokenEvent.getCustomProperties());
            const expiresInValue = getTokenExiresInValueOrDefault(tokenInfo.expiresIn);

            setPowerBiAccessToken(tokenInfo.accessToken);
            createAccessTokenInterval(expiresInValue);
        }, secondsLeft * 1000);

        setIntervalId((oldIntervalId) => {
            if (oldIntervalId !== undefined) {
                clearInterval(oldIntervalId);
            }
            return intervalId;
        });
    };

    const initialize = async () => {
        try {
            const microsoftTeamsAccessToken = await microsoftTeamsAccessTokenProvider.getAccessToken();
            const [tokenInfo, fetchedReports] = await Promise.all([
                powerBiReportsService.getPowerBiAccessToken(microsoftTeamsAccessToken),
                powerBiReportsService.getPowerBiReports(microsoftTeamsAccessToken),
            ]);

            const expiresInValue = getTokenExiresInValueOrDefault(tokenInfo.expiresIn);

            const gotTokenEvent = new GotPowerBiTokenEvent(tokenInfo.expiresIn, false);
            trackEvent(appInsights, gotTokenEvent, gotTokenEvent.getCustomProperties());

            const gotReportsEvent = new GotPowerBiReportsEvent(fetchedReports?.length ?? 0);
            trackEvent(appInsights, gotReportsEvent, gotReportsEvent.getCustomProperties());

            setPowerBiAccessToken(tokenInfo.accessToken);
            createAccessTokenInterval(expiresInValue);

            setReports(fetchedReports);
        } catch (error) {
            const errorMessage = JSON.stringify(error);
            trackException(appInsights, { error: new Error(errorMessage) });

            alert(`Failed to initialize PowerBI reports: '${errorMessage}'.`);
        } finally {
            setInitialized(true);
        }
    };

    const getTokenExiresInValueOrDefault = (expiresIn: number) => {
        let exponentialBackoff = parseInt(
            process.env.REACT_APP_POWER_BI_TOKEN_REQUEST_EXPONENTIAL_BACKOFF_IN_SEC ?? ""
        );
        if (isNaN(exponentialBackoff)) {
            exponentialBackoff = 1;
        }
        return expiresIn === 0 ? exponentialBackoff : expiresIn;
    };

    return (
        <PowerBiReportsContext.Provider
            value={{
                powerBiAccessToken: powerBiAccessToken,
                reports: reports,
                selectedReport: selectedReport,
                setSelectedReport: (report) => setSelectedReport(report),
                initialized: initialized,
                initialize: initialize,
                selectedReportPages: selectedReportPages,
                selectedReportPage: selectedReportPage,
                setSelectedReportPages: (pages) => setSelectedReportPages(pages),
                setSelectedReportPage: (page) => setSelectedReportPage(page),
            }}
        >
            {props.children}
        </PowerBiReportsContext.Provider>
    );
};

export default PowerBiReportsStore;
